import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, MessageSquare, ArrowUpRight, Music2, Sparkles, Disc3, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import AudioPlayer from "@/components/AudioPlayer";
import LangSwitcher from "@/components/LangSwitcher";
import { useI18n } from "@/i18n";

const PHONE = "+32 4 65 69 83 61";
const PHONE_HREF = "tel:+32465698361";
const SMS_HREF = "sms:+32465698361";
const EMAIL = "contacticelabel@gmail.com";
const ADMIN_SEQUENCE = "jesuisallechezlouis";

const HERO_BG =
  "https://images.unsplash.com/photo-1630754157718-8b7b3aaa57e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxyZWNvcmRpbmclMjBzdHVkaW8lMjBtaXhpbmclMjBkZXNrJTIwZGFyayUyMG1vb2R5fGVufDB8fHx8MTc3ODE0ODIwN3ww&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1532505282875-aff2e0311d4f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxzdHVkaW8lMjBtaWNyb3Bob25lJTIwZGFyayUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3NzgxNDgyMDd8MA&ixlib=rb-4.1.0&q=85";

const BEFORE_AFTER = [
  {
    key: "pop",
    off: "https://customer-assets-jai6qajn.emergentagent.net/job_ice-audio-lab/artifacts/gqpctkvr_POP%20INSTRU%20MIX%20OFF%20.wav",
    on: "https://customer-assets-jai6qajn.emergentagent.net/job_ice-audio-lab/artifacts/e00u42nj_INSTRU%20POP%20MIX%20ON%20.wav",
  },
  {
    key: "joy",
    off: "https://customer-assets-jai6qajn.emergentagent.net/job_ice-audio-lab/artifacts/ph0nmg1c_VOIX%20MIX%20JOY%20OFF.wav",
    on: "https://customer-assets-jai6qajn.emergentagent.net/job_ice-audio-lab/artifacts/fq48tk8u_VOIX%20MIX%20JOY%20ON.wav",
  },
  {
    key: "blue",
    off: "https://customer-assets-jai6qajn.emergentagent.net/job_ice-audio-lab/artifacts/yfv41d54_VOIX%20MIX%20BLUE%20OFF%20.wav",
    on: "https://customer-assets-jai6qajn.emergentagent.net/job_ice-audio-lab/artifacts/kqld50uq_MIX%20VOIX%20BLUE%20ON%20.wav",
  },
];

export default function Landing() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const yearRef = useRef(new Date().getFullYear());
  const [bufferHint, setBufferHint] = useState(0); // subtle visual cue (unused visible)
  const bufferRef = useRef("");
  const timerRef = useRef(null);

  useEffect(() => {
    document.title = "Ice Label — Where Music Lives";
  }, []);

  // Secret admin sequence: type "jesuisallechezlouis" anywhere on landing → /admin
  useEffect(() => {
    const onKey = (e) => {
      // Ignore if typing in an input/textarea
      const tag = (e.target && e.target.tagName) || "";
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) return;
      if (e.key.length !== 1) return; // only printable single chars
      const ch = e.key.toLowerCase();
      bufferRef.current = (bufferRef.current + ch).slice(-ADMIN_SEQUENCE.length);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        bufferRef.current = "";
      }, 3000);
      if (bufferRef.current === ADMIN_SEQUENCE) {
        bufferRef.current = "";
        navigate("/admin");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [navigate]);

  const services = [
    {
      icon: Disc3,
      eyebrow: t("services.mix.eyebrow"),
      title: t("services.mix.title"),
      price: "30€",
      unit: t("services.mix.unit"),
      desc: t("services.mix.desc"),
      note: t("services.mix.note"),
      span: "lg:col-span-2",
    },
    {
      icon: Sparkles,
      eyebrow: t("services.prod.eyebrow"),
      title: t("services.prod.title"),
      price: t("services.prod.price"),
      unit: "",
      desc: t("services.prod.desc"),
      span: "lg:col-span-1",
    },
    {
      icon: Music2,
      eyebrow: t("services.mastering.eyebrow"),
      title: t("services.mastering.title"),
      price: "50€",
      unit: t("services.mastering.unit"),
      desc: t("services.mastering.desc"),
      span: "lg:col-span-3",
    },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* HEADER */}
      <header
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <a href="#top" className="flex items-center gap-2 group flex-shrink-0" data-testid="logo-link">
            <span className="h-2 w-2 rounded-full bg-cyan-200 shadow-[0_0_12px_#A5F3FC] group-hover:shadow-[0_0_18px_#A5F3FC] transition-shadow" />
            <span className="font-heading text-lg tracking-tight font-medium">Ice Label</span>
          </a>
          <nav className="hidden md:flex items-center gap-8 lg:gap-10 text-sm text-zinc-400">
            <a href="#services" className="hover:text-white transition-colors" data-testid="nav-services">{t("nav.services")}</a>
            <a href="#extraits" className="hover:text-white transition-colors" data-testid="nav-extraits">{t("nav.extraits")}</a>
            <a href="#about" className="hover:text-white transition-colors" data-testid="nav-about">{t("nav.about")}</a>
            <a href="#contact" className="hover:text-white transition-colors" data-testid="nav-contact">{t("nav.contact")}</a>
          </nav>
          <div className="flex items-center gap-3 flex-shrink-0">
            <LangSwitcher />
            <a
              href={PHONE_HREF}
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.18em] hover:bg-white hover:text-black transition-all"
              data-testid="header-call-button"
            >
              <Phone className="h-3.5 w-3.5" /> {t("header.call")}
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section id="top" className="relative min-h-[100vh] flex items-end pb-20 pt-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Studio d'enregistrement" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#050505]" />
        </div>
        <div className="relative max-w-7xl w-full mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="fade-up text-xs tracking-[0.3em] uppercase text-cyan-200/90 font-mono-tech mb-6" data-testid="hero-eyebrow">
              {t("hero.eyebrow")}
            </p>
            <h1
              className="fade-up delay-1 font-heading font-extralight text-6xl sm:text-7xl lg:text-[8.5rem] leading-[0.92] tracking-[-0.04em]"
              data-testid="hero-title"
            >
              Ice Label
            </h1>
            <p
              className="fade-up delay-2 mt-4 font-heading italic text-cyan-200/90 text-xl sm:text-2xl tracking-wide"
              data-testid="hero-slogan"
            >
              — {t("hero.slogan")}
            </p>
            <p className="fade-up delay-2 mt-8 max-w-xl text-zinc-300 text-lg leading-relaxed" data-testid="hero-subtitle">
              {t("hero.subtitle")}
            </p>
            <div className="fade-up delay-3 mt-10 flex flex-wrap items-center gap-4">
              <a href={PHONE_HREF} data-testid="hero-call-button">
                <Button className="bg-white text-black hover:bg-cyan-200 hover:text-black rounded-full h-12 px-7 text-sm font-medium tracking-wide">
                  <Phone className="h-4 w-4 mr-2" /> {t("hero.callBtn")} — {PHONE}
                </Button>
              </a>
              <a href={SMS_HREF} data-testid="hero-sms-button">
                <Button
                  variant="outline"
                  className="bg-transparent border-white/25 text-white hover:bg-white hover:text-black rounded-full h-12 px-6 text-sm font-medium tracking-wide"
                >
                  <MessageSquare className="h-4 w-4 mr-2" /> {t("hero.smsBtn")}
                </Button>
              </a>
              <a
                href="#services"
                className="text-sm text-zinc-300 hover:text-white inline-flex items-center gap-2 group ml-1"
                data-testid="hero-services-link"
              >
                {t("hero.seeServices")}
                <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-4 fade-up delay-4">
            <div className="border border-white/10 rounded-2xl p-6 bg-black/40 backdrop-blur-md">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono-tech mb-3">{t("hero.reservationTitle")}</p>
              <p className="text-zinc-300 text-sm leading-relaxed">{t("hero.reservationText")}</p>
              <div className="mt-5 pt-5 border-t border-white/10">
                <p className="font-heading text-2xl tracking-tight">{PHONE}</p>
                <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-cyan-200/80 font-mono-tech">{t("hero.hours")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative py-32 lg:py-40 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 mb-20">
            <div className="lg:col-span-5">
              <p className="text-xs tracking-[0.3em] uppercase text-cyan-200/90 font-mono-tech mb-5">{t("services.eyebrow")}</p>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-[1.05]" data-testid="services-title">
                {t("services.title")}
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 self-end">
              <p className="text-zinc-400 text-base leading-relaxed">{t("services.desc")}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {services.map((s, idx) => (
              <article
                key={s.title}
                className={`group relative ${s.span} border border-white/10 rounded-2xl p-8 lg:p-10 bg-[#0F0F11] hover:border-cyan-200/40 transition-all duration-300 hover:-translate-y-1`}
                data-testid={`service-card-${idx}`}
              >
                <div className="flex items-center justify-between mb-10">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono-tech">{s.eyebrow}</span>
                  <s.icon className="h-5 w-5 text-cyan-200/80 group-hover:text-cyan-200 transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-3xl tracking-tight font-medium mb-4">{s.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-8 max-w-md">{s.desc}</p>
                <div className="flex items-baseline gap-2 pt-6 border-t border-white/10">
                  <span className="font-heading text-4xl lg:text-5xl tracking-tight font-light text-white" data-testid={`service-price-${idx}`}>
                    {s.price}
                  </span>
                  {s.unit && <span className="text-zinc-500 text-sm">{s.unit}</span>}
                </div>
                {s.note && (
                  <p className="mt-3 text-[11px] uppercase tracking-[0.15em] text-cyan-200/80 font-mono-tech" data-testid={`service-note-${idx}`}>
                    {s.note}
                  </p>
                )}
              </article>
            ))}
          </div>

          <p className="mt-10 text-center text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono-tech" data-testid="services-night-note">
            ⓘ&nbsp; {t("services.night")}
          </p>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section id="extraits" className="relative py-32 lg:py-40 px-6 lg:px-10 border-t border-white/5 bg-[#08080A]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-6">
              <p className="text-xs tracking-[0.3em] uppercase text-cyan-200/90 font-mono-tech mb-5">{t("ba.eyebrow")}</p>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-[1.05]" data-testid="ba-title">
                {t("ba.title")}
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 self-end">
              <p className="text-zinc-400 text-base leading-relaxed">{t("ba.subtitle")}</p>
            </div>
          </div>

          <div className="space-y-8" data-testid="before-after-list">
            {BEFORE_AFTER.map((track, idx) => (
              <article
                key={track.key}
                className="border border-white/10 rounded-2xl p-6 lg:p-8 bg-[#0F0F11]"
                data-testid={`ba-item-${idx}`}
              >
                <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
                  <h3 className="font-heading text-2xl tracking-tight font-medium">{t(`ba.${track.key}`)}</h3>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono-tech">{t(`ba.${track.key}.style`)}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <AudioPlayer src={track.off} label={t("ba.raw")} variant="off" testId={`ba-${idx}-off`} />
                  <AudioPlayer src={track.on} label={t("ba.mixed")} variant="on" testId={`ba-${idx}-on`} />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="relative py-32 lg:py-40 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="text-xs tracking-[0.3em] uppercase text-cyan-200/90 font-mono-tech mb-5">{t("about.eyebrow")}</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-[1.05] mb-8" data-testid="about-title">
              {t("about.title")}
            </h2>
            <div className="space-y-5 text-zinc-400 text-base leading-relaxed">
              <p>{t("about.p1")}</p>
              <p>{t("about.p2")}</p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[
                { k: t("about.sessions"), v: t("about.sessionsV") },
                { k: t("about.matos"), v: t("about.matosV") },
                { k: t("about.loc"), v: t("about.locV") },
              ].map((it) => (
                <div key={it.k} className="border-l border-white/10 pl-4">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono-tech">{it.k}</p>
                  <p className="font-heading text-base mt-1">{it.v}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 relative">
            <div className="relative aspect-[4/5] lg:aspect-[5/6] rounded-2xl overflow-hidden border border-white/10">
              <img src={ABOUT_IMG} alt="Microphone studio" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#050505] via-transparent to-transparent opacity-60" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden lg:flex items-center gap-3 bg-[#0F0F11] border border-white/10 rounded-full px-5 py-3">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs uppercase tracking-[0.2em] font-mono-tech text-zinc-300">{t("about.badge")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="relative py-32 lg:py-40 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-[0.3em] uppercase text-cyan-200/90 font-mono-tech mb-6">{t("contact.eyebrow")}</p>
          <h2 className="font-heading text-5xl sm:text-7xl lg:text-8xl tracking-tighter font-light leading-[0.95] mb-6" data-testid="contact-title">
            {t("contact.title")}
          </h2>
          <p className="text-zinc-400 text-base max-w-xl mb-10">{t("contact.subtitle")}</p>

          {/* Hours strip */}
          <div
            className="mb-10 border border-white/10 rounded-2xl bg-[#0F0F11] p-6 lg:p-7 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
            data-testid="hours-card"
          >
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex h-10 w-10 rounded-lg border border-white/10 items-center justify-center bg-cyan-200/5 flex-shrink-0">
                <Clock className="h-4 w-4 text-cyan-200" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono-tech mb-1">
                  {t("contact.hours.label")}
                </p>
                <p className="font-heading text-xl sm:text-2xl tracking-tight font-medium" data-testid="hours-value">
                  {t("contact.hours.value")}
                </p>
                <p className="text-xs text-zinc-500 mt-1">{t("contact.hours.sub")}</p>
              </div>
            </div>
            <div className="lg:text-right lg:pl-6 lg:border-l border-white/10">
              <p className="text-[10px] uppercase tracking-[0.25em] text-cyan-200/90 font-mono-tech mb-1">
                ⓘ Night rate
              </p>
              <p className="text-sm text-zinc-300" data-testid="hours-night">
                {t("contact.hours.night")}
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            <a
              href={PHONE_HREF}
              className="lg:col-span-7 group block border border-white/10 hover:border-cyan-200/50 rounded-2xl p-10 bg-[#0F0F11] transition-all"
              data-testid="contact-phone-card"
            >
              <div className="flex items-center justify-between mb-8">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono-tech">
                  <Phone className="h-3.5 w-3.5" /> {t("contact.call")}
                </span>
                <ArrowUpRight className="h-5 w-5 text-zinc-500 group-hover:text-cyan-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
              </div>
              <p className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light group-hover:text-cyan-200 transition-colors" data-testid="contact-phone-number">
                {PHONE}
              </p>
              <p className="mt-4 text-sm text-zinc-500">{t("contact.callSub")}</p>
            </a>

            <div className="lg:col-span-5 grid gap-6">
              <a
                href={SMS_HREF}
                className="group border border-white/10 hover:border-cyan-200/50 rounded-2xl p-8 bg-[#0F0F11] transition-all"
                data-testid="contact-sms-card"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono-tech">
                    <MessageSquare className="h-3.5 w-3.5" /> {t("contact.sms")}
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-zinc-500 group-hover:text-cyan-200 transition-all" />
                </div>
                <p className="font-heading text-2xl sm:text-3xl tracking-tight font-light group-hover:text-cyan-200 transition-colors" data-testid="contact-sms-number">
                  {PHONE}
                </p>
                <p className="mt-2 text-xs text-zinc-500">{t("contact.smsSub")}</p>
              </a>

              <a
                href={`mailto:${EMAIL}`}
                className="group border border-white/10 hover:border-white/30 rounded-2xl p-8 bg-[#0F0F11] transition-all"
                data-testid="contact-email-card"
              >
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono-tech mb-4">
                  <Mail className="h-3.5 w-3.5" /> {t("contact.email")}
                </div>
                <p className="font-heading text-lg sm:text-xl tracking-tight break-all group-hover:text-cyan-200 transition-colors" data-testid="contact-email">
                  {EMAIL}
                </p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 py-10 px-6 lg:px-10" data-testid="site-footer">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-cyan-200" />
            <span className="font-heading text-base tracking-tight">Ice Label</span>
            <span className="text-xs text-zinc-600 ml-3">© {yearRef.current}</span>
            <span className="text-xs text-zinc-600 ml-3 italic">Where Music Lives</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-zinc-500">
            <a href={PHONE_HREF} className="hover:text-white transition-colors">{PHONE}</a>
            <a href={`mailto:${EMAIL}`} className="hover:text-white transition-colors hidden sm:inline">{EMAIL}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
